import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../types';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';

const LOGIN_401_KEY = 'login_401_message';

function getBackendErrorMessage(err: any): string {
  const code = err?.code as string | undefined;
  const errMessage = err?.message as string | undefined;
  if (code === 'ERR_NETWORK' || errMessage === 'Network Error') {
    return 'ارتباط با سرور برقرار نشد. بک‌اند Nest را با «npm run start:dev» در پوشهٔ backend روی پورت ۳۰۰۰ اجرا کنید و پنل را با «npm start» در پوشهٔ admin-panel باز کنید (یا آدرس API را در تنظیمات سرور/nginx بررسی کنید).';
  }
  if (err?.response?.status === 404) {
    return 'آدرس ورود (API) یافت نشد (۴۰۴). بک‌اند را روی پورت ۳۰۰۰ اجرا کنید؛ آدرس باید به «http://127.0.0.1:3000/api/auth/login» برسد. اگر پنل را با build استاتیک باز کرده‌اید، از «npm start» برای توسعه استفاده کنید یا REACT_APP_API_URL را درست تنظیم کنید.';
  }
  const data = err?.response?.data;
  if (!data) return errMessage || code || 'ورود ناموفق';
  const bodyMessage = data.message;
  if (Array.isArray(bodyMessage)) return bodyMessage.join(' ');
  if (typeof bodyMessage === 'string' && bodyMessage.trim()) return bodyMessage.trim();
  return data.error || err?.message || 'ورود ناموفق';
}

type LoginType = 'admin' | 'sales' | 'seller' | 'mentor';

/**
 * تب‌های ورود فقط برچسب و راهنمای ورودی را عوض می‌کنند.
 * سرور نقش را از خود حساب می‌خواند، نه از این انتخاب — پس انتخاب اشتباه تب
 * باعث نمی‌شود ورود شکست بخورد.
 */
const LOGIN_TABS: Array<{ key: LoginType; label: string; hint: string; placeholder: string }> = [
  { key: 'admin', label: 'مدیر', hint: 'ایمیل، نام کاربری یا موبایل', placeholder: 'admin@haghighi.com' },
  { key: 'sales', label: 'مدیر فروش', hint: 'شماره تلفن همراه', placeholder: '09123456789' },
  { key: 'seller', label: 'فروشنده', hint: 'شماره تلفن همراه', placeholder: '09123456790' },
  { key: 'mentor', label: 'منتور', hint: 'موبایل یا نام کاربری', placeholder: '09123456789' },
];

const Login: React.FC = () => {
  const [loginType, setLoginType] = useState<LoginType>('admin');
  const [credentials, setCredentials] = useState<LoginCredentials>({
    login: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setShowErrorModal(false);
    setErrorModalMessage('');

    try {
      /**
       * هر ورودی‌ای که فقط رقم باشد به‌عنوان شمارهٔ موبایل نرمال می‌شود — صرف‌نظر
       * از تب انتخاب‌شده. قبلاً تب «مدیر» مستثنا بود و مدیری که با موبایل وارد
       * می‌شد، شماره‌اش نرمال‌نشده به سرور می‌رفت.
       */
      const normalizedCredentials = {
        ...credentials,
        login: /^[\d۰-۹٠-٩+\s-]+$/.test(credentials.login.trim())
          ? normalizePhoneNumber(credentials.login.trim())
          : credentials.login.trim(),
      };
      await login(normalizedCredentials);
      // مقصد را نقش تعیین می‌کند؛ مسیر «/» خودش کاربر را به صفحهٔ خودش می‌برد
      navigate('/');
    } catch (err: any) {
      setErrorModalMessage(getBackendErrorMessage(err));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorModalMessage('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4 font-sans antialiased">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              ورود به پنل مدیریت
            </h1>
            <p className="text-gray-600">پلتفرم حقیقی</p>
          </div>

          <div className="flex justify-center mb-6">
            <div className="bg-gray-100 rounded-lg p-1 flex flex-wrap justify-center gap-1">
              {LOGIN_TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setLoginType(item.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    loginType === item.key
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="login" className="block text-sm font-medium text-gray-700 mb-2">
                {LOGIN_TABS.find((t) => t.key === loginType)?.hint ?? 'ایمیل یا شماره موبایل'}
              </label>
              {/*
                نوع فیلد عمداً text است نه email.
                سرور نام کاربری، ایمیل و موبایل را می‌پذیرد؛ با type="email"
                مرورگر ورود با موبایل یا نام کاربری را قبل از ارسال رد می‌کرد.
              */}
              <input
                id="login"
                name="login"
                type="text"
                inputMode={loginType === 'admin' ? 'text' : 'tel'}
                autoComplete="username"
                required
                value={credentials.login}
                onChange={handleChange}
                disabled={loading}
                dir="ltr"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                placeholder={LOGIN_TABS.find((t) => t.key === loginType)?.placeholder}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                رمز عبور
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={credentials.password}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="رمز عبور خود را وارد کنید"
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <BrandSpinnerRing size="md" inverse />
                  در حال ورود...
                </div>
              ) : (
                'ورود'
              )}
            </button>
          </form>

          {/* <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              <strong>اطلاعات آزمایشی:</strong>
            </p>
            {loginType === 'admin' ? (
              <div className="text-sm text-gray-600">
                <p>ایمیل: admin@haghighi.com</p>
                <p>رمز عبور: admin123</p>
              </div>
            ) : loginType === 'sales' ? (
              <div className="text-sm text-gray-600">
                <p>شماره تلفن: 09123456789</p>
                <p>رمز عبور: sales123</p>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                <p>شماره تلفن: 09123456790</p>
                <p>رمز عبور: sales123</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              مدیران با ایمیل، بقیه با شماره تلفن وارد می‌شوند
            </p>
          </div> */}
        </div>
      </div>

      {/* مودال خطای بک‌اند */}
      {showErrorModal && errorModalMessage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="error-modal-title"
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border border-red-100">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h2 id="error-modal-title" className="text-lg font-semibold text-gray-900 mb-1">
                  خطا
                </h2>
                <p className="text-gray-700 text-right leading-relaxed">
                  {errorModalMessage}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={closeErrorModal}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
              >
                متوجه شدم
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;